"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const TC = {
    sel: "text-sky-300",
    prop: "text-gray-300",
    val: "text-amber-300",
    var: "text-pink-300",
    kw: "text-fuchsia-400",
    punct: "text-gray-500",
    str: "text-emerald-300",
};

const EXAMPLES = [
    {
        key: "variables",
        tabKey: "Sass_compile_tab_variables",
        scss: [
            [["$primary", "var"], [": ", "punct"], ["#ec4899", "str"], [";", "punct"]],
            [[".btn", "sel"], [" { ", "punct"], ["background", "prop"], [": ", "punct"], ["$primary", "var"], ["; }", "punct"]],
            [[".link", "sel"], [" { ", "punct"], ["color", "prop"], [": ", "punct"], ["$primary", "var"], ["; }", "punct"]],
        ],
        css: [
            [[".btn", "sel"], [" { ", "punct"], ["background", "prop"], [": ", "punct"], ["#ec4899", "str"], ["; }", "punct"]],
            [[".link", "sel"], [" { ", "punct"], ["color", "prop"], [": ", "punct"], ["#ec4899", "str"], ["; }", "punct"]],
        ],
    },
    {
        key: "nesting",
        tabKey: "Sass_compile_tab_nesting",
        scss: [
            [[".card", "sel"], [" {", "punct"]],
            [["  padding", "prop"], [": ", "punct"], ["16px", "val"], [";", "punct"]],
            [["  .title", "sel"], [" { ", "punct"], ["font-weight", "prop"], [": ", "punct"], ["bold", "val"], ["; }", "punct"]],
            [["  &:hover", "var"], [" { ", "punct"], ["transform", "prop"], [": ", "punct"], ["scale(1.02)", "val"], ["; }", "punct"]],
            [["}", "punct"]],
        ],
        css: [
            [[".card", "sel"], [" { ", "punct"], ["padding", "prop"], [": ", "punct"], ["16px", "val"], ["; }", "punct"]],
            [[".card .title", "sel"], [" { ", "punct"], ["font-weight", "prop"], [": ", "punct"], ["bold", "val"], ["; }", "punct"]],
            [[".card:hover", "sel"], [" { ", "punct"], ["transform", "prop"], [": ", "punct"], ["scale(1.02)", "val"], ["; }", "punct"]],
        ],
    },
    {
        key: "loop",
        tabKey: "Sass_compile_tab_loop",
        scss: [
            [["@for", "kw"], [" $i ", "var"], ["from", "kw"], [" 1 ", "val"], ["through", "kw"], [" 3 {", "punct"]],
            [["  .m-", "sel"], ["#{$i}", "var"], [" { ", "punct"], ["margin", "prop"], [": ", "punct"], ["#{$i * 8}px", "var"], ["; }", "punct"]],
            [["}", "punct"]],
        ],
        css: [
            [[".m-1", "sel"], [" { ", "punct"], ["margin", "prop"], [": ", "punct"], ["8px", "val"], ["; }", "punct"]],
            [[".m-2", "sel"], [" { ", "punct"], ["margin", "prop"], [": ", "punct"], ["16px", "val"], ["; }", "punct"]],
            [[".m-3", "sel"], [" { ", "punct"], ["margin", "prop"], [": ", "punct"], ["24px", "val"], ["; }", "punct"]],
        ],
    },
];

const cssContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
};
const cssLine = {
    hidden: { opacity: 0, x: -12 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3, ease: "easeOut" } },
};

function lineTokens(line) {
    return line.map((tok, ti) => (
        <span key={ti} className={TC[tok[1]]}>
            {tok[0]}
        </span>
    ));
}

export default function SassCompile() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const [active, setActive] = useState(0);
    const ex = EXAMPLES[active];

    return (
        <section id="sass-compile" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Sass_compile_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Sass_compile_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-pink-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:sass"
                            className="w-4 h-4"
                            aria-hidden="true"
                        />
                        styles.scss
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Sass_compile_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8">
                    {/* Onglets de concepts */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        {EXAMPLES.map((e, i) => (
                            <button
                                key={e.key}
                                type="button"
                                onClick={() => setActive(i)}
                                className={`relative rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors active:scale-95 ${
                                    active === i
                                        ? "text-pink-200"
                                        : "text-gray-400 hover:text-gray-200"
                                }`}
                            >
                                {active === i && (
                                    <motion.span
                                        layoutId="sassCompileTab"
                                        className="absolute inset-0 rounded-full bg-pink-500/15 ring-1 ring-pink-400/50"
                                        transition={{
                                            type: "spring",
                                            stiffness: 420,
                                            damping: 34,
                                        }}
                                    />
                                )}
                                <span className="relative z-10">
                                    {t(e.tabKey)}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="grid md:grid-cols-[1fr_2.5rem_1fr] gap-3 items-stretch">
                        {/* SCSS */}
                        <div className="min-w-0 rounded-2xl bg-black/40 ring-1 ring-white/10 overflow-hidden">
                            <div className="flex items-center gap-1.5 px-4 py-2 border-b border-white/10 text-[11px] font-mono text-gray-400">
                                <span className="w-2 h-2 rounded-full bg-pink-400" />
                                styles.scss
                            </div>
                            <motion.div
                                key={ex.key}
                                initial={reduce ? false : { opacity: 0, x: -6 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.25, ease: "easeOut" }}
                                className="p-4 text-[12.5px] leading-6 font-mono overflow-x-auto"
                            >
                                {ex.scss.map((line, li) => (
                                    <div key={li} className="whitespace-pre">
                                        {lineTokens(line)}
                                    </div>
                                ))}
                            </motion.div>
                        </div>

                        {/* Flèche de compilation (pulse à chaque changement) */}
                        <div className="hidden md:flex items-center justify-center">
                            <motion.span
                                key={ex.key}
                                initial={
                                    reduce
                                        ? false
                                        : { scale: 0.6, opacity: 0.4 }
                                }
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 380,
                                    damping: 16,
                                    delay: 0.12,
                                }}
                                className="flex items-center justify-center w-9 h-9 rounded-full bg-pink-500/15 ring-1 ring-pink-400/30"
                            >
                                <SolarIcon
                                    name="alt-arrow-right-bold"
                                    className="w-4 h-4 text-pink-300"
                                />
                            </motion.span>
                        </div>

                        {/* CSS (compilation ligne par ligne) */}
                        <div className="min-w-0 rounded-2xl bg-black/40 ring-1 ring-white/10 overflow-hidden">
                            <div className="flex items-center gap-1.5 px-4 py-2 border-b border-white/10 text-[11px] font-mono text-gray-400">
                                <span className="w-2 h-2 rounded-full bg-sky-400" />
                                styles.css
                            </div>
                            <motion.div
                                key={ex.key}
                                variants={cssContainer}
                                initial={reduce ? false : "hidden"}
                                animate="visible"
                                className="p-4 text-[12.5px] leading-6 font-mono overflow-x-auto"
                            >
                                {ex.css.map((line, li) => (
                                    <motion.div
                                        key={li}
                                        variants={cssLine}
                                        className="whitespace-pre"
                                    >
                                        {lineTokens(line)}
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Sass_compile_caption")}
            </p>
        </section>
    );
}
